import type {
  PaigeEmailChannel,
  PaigeEmailSettings,
  PaigeEmailSource,
  PaigeInboundMessage,
} from './paige.global';
import { responseWords } from './problem-words';

/**
 * The job's email source through the job's channel (`{engineOrigin}` is `/v1/jobs/{id}`): read it,
 * save its settings, list what it received, and release a hold. The email window is the one place a person sees and
 * changes email for a job (Dustin, 2026-09-24).
 */
export function emailChannel(engineOrigin: string, tenant: string | undefined): PaigeEmailChannel {
  const call = (path: string, init: { method?: string; body?: unknown } = {}) =>
    fetch(`${engineOrigin}/sources${path}`, {
      method: init.method ?? 'GET',
      credentials: 'include',
      headers: headersFor(tenant, init.method !== undefined),
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  const answered = async (response: Response): Promise<PaigeEmailSource> => {
    if (!response.ok) throw new Error(await reasonOf(response));
    return sourceOf((await response.json()) as SourceRow);
  };
  const at = (id: string) => `/${encodeURIComponent(id)}`;
  return {
    async source() {
      const response = await call('');
      if (!response.ok) return null;
      const row = ((await response.json()) as SourceRow[]).find((r) => r.kind === 'email');
      return row ? sourceOf(row) : null;
    },
    save: async (current, settings) =>
      answered(await call(at(current.id), { method: 'PUT', body: bodyOf(current, settings) })),
    async messages(sourceId) {
      const response = await call(`${at(sourceId)}/messages`);
      return response.ok ? ((await response.json()) as PaigeInboundMessage[]) : [];
    },
    release: async (sourceId) =>
      answered(await call(`${at(sourceId)}/release`, { method: 'POST' })),
  };
}

/** The tenant on every call; a write also carries its JSON type and a fresh idempotency key. */
function headersFor(tenant: string | undefined, write: boolean): Record<string, string> {
  return {
    ...(tenant ? { 'Paige-Tenant': tenant } : {}),
    ...(write
      ? { 'content-type': 'application/json', 'Idempotency-Key': crypto.randomUUID() }
      : {}),
  };
}

interface SourceRow {
  id: string;
  kind: string;
  label: string;
  detail: string;
  lastActivityAt: string | null;
  holdReason?: unknown;
  enabled?: boolean;
  allowedSenders?: string[];
  readBody?: boolean;
  readHtml?: boolean;
}

function sourceOf(row: SourceRow): PaigeEmailSource {
  return {
    id: row.id,
    label: row.label,
    address: row.detail,
    lastActivityAt: row.lastActivityAt,
    held: Boolean(row.holdReason),
    enabled: row.enabled !== false,
    allowedSenders: row.allowedSenders ?? [],
    readBody: row.readBody ?? false,
    readHtml: row.readHtml ?? false,
  };
}

/** The contract's SourceInput: everything the source keeps, with this change applied. */
function bodyOf(current: PaigeEmailSource, settings: Partial<PaigeEmailSettings>) {
  const next = { ...current, ...settings };
  return {
    kind: 'email',
    label: next.label,
    detail: next.address,
    allowedSenders: next.allowedSenders,
    enabled: next.enabled,
    readBody: next.readBody,
    readHtml: next.readHtml,
  };
}

function reasonOf(response: Response): Promise<string> {
  return responseWords(response, 'The change was not saved. Try again in a minute.');
}
